import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { TABLES } from '../../lib/constants';
import { useAuth } from '../../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiShield, FiUser, FiPlus, FiTrash2, FiUsers, FiEdit3, FiX, FiCheck, FiSave, FiSearch, FiExternalLink, FiAlertCircle } = FiIcons;

const AdminDashboard = () => {
  const { user, impersonate } = useAuth();
  const [data, setData] = useState({ masters: [], pilots: [] });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [notification, setNotification] = useState(null);

  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({ username: '', nickname: '', role: 'master' });

  useEffect(() => {
    loadAllData();
  }, []);

  const notify = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      const { data: users } = await supabase.from(TABLES.USERS).select('*');
      const { data: pmData } = await supabase.from(TABLES.PILOT_MASTERS).select('*, master:master_id(id, username, nickname)');
      
      if (users) {
        const pilots = users.filter(u => u.role === 'pilot').map(p => ({
          ...p,
          contracts: (pmData || []).filter(pm => pm.pilot_id === p.id)
        }));
        setData({ masters: users.filter(u => u.role === 'master'), pilots });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openUserModal = (userToEdit = null) => {
    if (userToEdit) {
      setEditingUser(userToEdit);
      setUserFormData({ username: userToEdit.username, nickname: userToEdit.nickname || '', role: userToEdit.role });
    } else {
      setEditingUser(null);
      setUserFormData({ username: '', nickname: '', role: 'master' });
    }
    setIsUserModalOpen(true);
  };

  const handleSaveUser = async () => {
    if (!userFormData.username.trim()) return;
    setLoading(true);
    try {
      const payload = {
        username: userFormData.username.trim().toLowerCase(),
        nickname: userFormData.nickname.trim(),
        role: userFormData.role
      };
      
      let error;
      if (editingUser) {
        const { error: err } = await supabase.from(TABLES.USERS).update(payload).eq('id', editingUser.id);
        error = err;
      } else {
        const { error: err } = await supabase.from(TABLES.USERS).insert([payload]);
        error = err;
      }
      
      if (error) throw error;
      notify(editingUser ? 'Account updated!' : 'Account created successfully!');
      setIsUserModalOpen(false);
      loadAllData();
    } catch (err) {
      notify(err.message.includes('unique') ? 'Username already exists' : err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const filteredPilots = data.pilots.filter(p => 
    p.username.toLowerCase().includes(searchTerm.toLowerCase()) || 
    (p.nickname || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-12 pb-20 relative">
      <AnimatePresence>
        {notification && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9 }} className={`fixed top-6 right-6 z-[110] px-6 py-4 rounded-2xl shadow-2xl border flex items-center gap-3 backdrop-blur-md ${notification.type === 'success' ? 'bg-emerald-500/90 text-white border-emerald-400' : 'bg-red-500/90 text-white border-red-400'}`}>
            <SafeIcon icon={notification.type === 'success' ? FiCheck : FiAlertCircle} />
            <span className="font-black text-xs uppercase tracking-widest">{notification.message}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <header className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h2 className="text-5xl font-black text-gray-900 tracking-tight">System Control</h2>
          <p className="text-gray-400 font-bold text-lg mt-2">Global Oversight of all Masters and Pilots</p>
        </div>
        <button onClick={() => openUserModal()} className="apple-button-primary flex items-center justify-center gap-2 shadow-xl !py-4 md:!py-5 !px-8 text-sm md:text-base">
          <FiPlus className="text-xl" /> Create Account
        </button>
      </header>

      <div className="grid grid-cols-1 gap-10">
        <div className="bento-card">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-indigo-600 text-white rounded-2xl flex items-center justify-center shadow-lg">
                <FiUsers className="text-2xl" />
              </div>
              <div>
                <h3 className="text-2xl font-black text-gray-900">Pilot Directory</h3>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Total: {data.pilots.length} pilots</p>
              </div>
            </div>
            <div className="relative w-full md:w-96">
              <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" placeholder="Search by username..." className="apple-input !pl-12 !py-4 bg-gray-50" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
          </div>

          <div className="overflow-x-auto no-scrollbar -mx-8 px-8">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="pb-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">Username</th>
                  <th className="pb-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">Nickname</th>
                  <th className="pb-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">Master Teams</th>
                  <th className="pb-6 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredPilots.map(p => (
                  <tr key={p.id} className="group hover:bg-gray-50/50 transition-colors">
                    <td className="py-6"><span className="font-black text-blue-600">@{p.username}</span></td>
                    <td className="py-6"><span className="font-bold text-gray-900">{p.nickname || '-'}</span></td>
                    <td className="py-6">
                      <div className="flex flex-wrap gap-1.5">
                        {p.contracts.length === 0 ? <span className="text-[10px] text-gray-300 italic">No Masters</span> : p.contracts.map(c => (
                          <span key={c.id} className={`px-2 py-0.5 bg-white border border-gray-200 rounded-md text-[9px] font-black uppercase ${c.status === 'pending' ? 'text-amber-500 border-amber-100' : 'text-gray-500'}`}>
                            {c.master?.nickname || c.master?.username} {c.status === 'pending' && '(Pending)'}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-6 text-right">
                      <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => openUserModal(p)} className="p-2 text-blue-600 bg-white shadow-sm border border-gray-100 rounded-xl hover:bg-blue-600 hover:text-white transition-all"><FiEdit3 /></button>
                        <button onClick={() => impersonate(p)} className="p-2 text-indigo-600 bg-white shadow-sm border border-gray-100 rounded-xl hover:bg-indigo-600 hover:text-white transition-all"><FiExternalLink /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bento-card">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-12 h-12 bg-gray-900 text-white rounded-2xl flex items-center justify-center shadow-lg">
              <FiShield className="text-2xl" />
            </div>
            <div>
              <h3 className="text-2xl font-black text-gray-900">Masters</h3>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Total: {data.masters.length} active masters</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.masters.map(m => (
              <div key={m.id} className="p-6 bg-gray-50 rounded-[2rem] border border-transparent hover:border-blue-100 transition-all flex items-center justify-between group">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-gray-400 group-hover:text-blue-600 transition-all shadow-sm">
                    <FiUser className="text-xl" />
                  </div>
                  <div>
                    <h4 className="font-black text-gray-900">{m.nickname || m.username}</h4>
                    <p className="text-[10px] font-black text-blue-500 uppercase tracking-widest">@{m.username}</p>
                  </div>
                </div>
                <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                   <button onClick={() => impersonate(m)} className="p-2.5 bg-white text-blue-600 rounded-xl shadow-sm hover:bg-blue-50"><FiExternalLink /></button>
                   <button onClick={() => openUserModal(m)} className="p-2.5 bg-white text-gray-600 rounded-xl shadow-sm hover:bg-gray-50"><FiEdit3 /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isUserModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-md">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="w-full max-w-2xl bg-white rounded-[2.5rem] p-8 md:p-12 shadow-2xl overflow-y-auto max-h-[90vh]">
              <div className="flex justify-between items-center mb-10">
                <h3 className="text-2xl font-black">{editingUser ? 'Edit Account' : 'Create Account'}</h3>
                <button onClick={() => setIsUserModalOpen(false)} className="p-2 bg-gray-100 rounded-full"><FiX /></button>
              </div>
              <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Username</label>
                    <input className="apple-input" value={userFormData.username} onChange={e => setUserFormData({ ...userFormData, username: e.target.value })} placeholder="e.g., joe_pilot" />
                  </div>
                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Display Nickname</label>
                    <input className="apple-input" value={userFormData.nickname} onChange={e => setUserFormData({ ...userFormData, nickname: e.target.value })} placeholder="e.g., Joe the Farmer" />
                  </div>
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Role</label>
                  <select className="apple-input" value={userFormData.role} onChange={e => setUserFormData({ ...userFormData, role: e.target.value })}>
                    <option value="master">Master</option>
                    <option value="pilot">Pilot</option>
                  </select>
                </div>
                <div className="pt-10 flex flex-col md:flex-row gap-4">
                  <button onClick={handleSaveUser} disabled={loading} className="apple-button-primary flex-1">
                    {loading ? 'Processing...' : (editingUser ? 'Save Updates' : 'Create Account')}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminDashboard;